import { RelayPageDataTable } from "@constellation-academy/epic-ui";
import { useContext } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "@i18n/hooks";
import { productsTable_ProductFragment$key } from "@relay/productsTable_ProductFragment.graphql";
import { productsTable_QueryFragment$key } from "@relay/productsTable_QueryFragment.graphql";
import { type productsTable_QueryFragmentRefetch } from "@relay/productsTable_QueryFragmentRefetch.graphql";
import { PRODUCTS_PER_PAGE } from "./products-table.const";
import { QUERY_FRAGMENT, PRODUCT_FRAGMENT } from "./products-table.graphql";
import { type ProductsTableProps } from "./products-table.types";
import { useHasPermissions } from "../../../../hooks/use-has-permissions";
import { selectActiveFeatureToggles } from "../../../../store/slices/FeatureTogglesSlice";
import { ProductsScreenContext } from "../../products.context";
import { ProductPriceColumn } from "../product-price-column";
import { ProductActionColumn } from "../ProductActionColumn";
import { ProductIdColumn } from "../ProductIdColumn";
import { ProductIsHiddenColumn } from "../ProductIsHiddenColumn";
import { ProductTitleColumn } from "../ProductTitleColumn";
import { ProductTypeColumn } from "../ProductTypeColumn";

export const ProductsTable = ({ productsQueryFragment }: ProductsTableProps) => {
	const { t } = useTranslation("common");
	const { filters, connectionId, setConnectionId } = useContext(ProductsScreenContext);
	const activeFeatureToggles = useSelector(selectActiveFeatureToggles);

	const handleOnConnectionIdChange = (id?: string | null) => {
		id && setConnectionId(id);
	};
	const canModify = useHasPermissions(["UserInAccountPermission_ProductAdmin_Modify"]);
	const hasSplitCourseCertification = activeFeatureToggles?.includes("splitCourseCertification");
	return (
		<>
			<RelayPageDataTable<
				productsTable_QueryFragmentRefetch,
				productsTable_QueryFragment$key,
				productsTable_ProductFragment$key
			>
				queryFragment={QUERY_FRAGMENT}
				parentFragmentRef={productsQueryFragment}
				nodeInlineFragment={PRODUCT_FRAGMENT}
				extractConnection={(d) => d.Admin.Billing.SearchProducts}
				rowsCount={PRODUCTS_PER_PAGE}
				filters={filters}
				onConnectionIdChange={handleOnConnectionIdChange}
			>
				{(Column) => (
					<>
						<Column
							header={t("table.id")}
							body={(item) => <ProductIdColumn productFragmentRef={item} />}
						/>
						<Column
							header={t("product.title")}
							body={(item) => <ProductTitleColumn productFragmentRef={item} />}
						/>
						<Column
							header={t("product.hidden")}
							body={(item) => <ProductIsHiddenColumn productFragmentRef={item} />}
						/>
						<Column
							header={t("price.price")}
							body={(item) => <ProductPriceColumn productFragmentRef={item} />}
						/>
						{hasSplitCourseCertification && (
							<Column
								header={t("product.licenseType")}
								body={(item) => <ProductTypeColumn productFragmentRef={item} />}
							/>
						)}
						{canModify && (
							<Column
								header={t("table.actions")}
								body={(item) => (
									<ProductActionColumn
										productFragmentRef={item}
										connectionId={connectionId}
									/>
								)}
							/>
						)}
					</>
				)}
			</RelayPageDataTable>
		</>
	);
};
